<div class="main-view space-y-2 md:space-y-4">
    <div class="flex flex-wrap items-start gap-2">
        <div class="min-w-0 flex-1 space-y-1">
            <app-page-surface-header titleIcon="chart"
                [compact]="true"
                [title]="summary?.agent_name || 'Report details'"
                subtitle="Session-level activity for this agent (same detail view as Audit Trail)."
            />
            <p *ngIf="summary?.agent_id" class="text-xs text-gray-500 dark:text-gray-400">
                Agent ID: <span class="font-mono">{{ summary!.agent_id }}</span>
            </p>
        </div>
    </div>

    <div
        *ngIf="loadError"
        class="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800 dark:border-red-900/40 dark:bg-red-950/40 dark:text-red-200"
        role="alert"
    >
        {{ loadError }}
        <div class="mt-4">
            <a [routerLink]="backToReports()" [queryParams]="backQueryParams()" class="btn inline-flex rounded-full bg-white px-4 py-2 text-black">
                Back to reports
            </a>
        </div>
    </div>

    <div *ngIf="loading && !summary && !loadError" class="mt-1 flex flex-col gap-3 py-4">
        <div *ngFor="let i of [1,2,3,4,5]" class="h-10 animate-pulse rounded-xl bg-gray-100 dark:bg-white/5"></div>
    </div>

    <ng-container *ngIf="summary && !loadError">
        <div class="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
            <div class="rounded-2xl border border-gray-200 bg-white/70 p-4 dark:border-[#191e3a] dark:bg-[#0e1726]">
                <p class="text-xs font-medium uppercase tracking-wide text-gray-500 dark:text-gray-400">Total PDs</p>
                <p class="mt-1 text-2xl font-bold tabular-nums text-gray-900 dark:text-white">{{ summary.total_pds }}</p>
            </div>
            <div class="rounded-2xl border border-gray-200 bg-white/70 p-4 dark:border-[#191e3a] dark:bg-[#0e1726]">
                <p class="text-xs font-medium uppercase tracking-wide text-gray-500 dark:text-gray-400">Duration</p>
                <p class="mt-1 text-2xl font-bold tabular-nums text-gray-900 dark:text-white">{{ durationDisplay(summary.pd_duration_seconds, summary.pd_duration_hours) }}</p>
            </div>
            <div class="rounded-2xl border border-gray-200 bg-white/70 p-4 dark:border-[#191e3a] dark:bg-[#0e1726]">
                <p class="text-xs font-medium uppercase tracking-wide text-gray-500 dark:text-gray-400">Pass / Review / Fail</p>
                <p class="mt-1 text-lg font-semibold tabular-nums text-gray-900 dark:text-white">
                    <span class="text-emerald-600 dark:text-emerald-400">{{ summary.pass_count }}</span>
                    /
                    <span class="text-amber-600 dark:text-amber-400">{{ summary.review_count }}</span>
                    /
                    <span class="text-red-600 dark:text-red-400">{{ summary.fail_count }}</span>
                </p>
            </div>
            <div class="rounded-2xl border border-gray-200 bg-white/70 p-4 dark:border-[#191e3a] dark:bg-[#0e1726]">
                <p class="text-xs font-medium uppercase tracking-wide text-gray-500 dark:text-gray-400">Client</p>
                <p class="mt-1 truncate text-sm font-semibold text-gray-900 dark:text-white" [title]="summary.client_name">{{ summary.client_name }}</p>
                <p class="mt-0.5 text-xs text-gray-500">{{ summary.product_type }}</p>
            </div>
        </div>

        <app-page-body-panel>
            <ng-container ngProjectAs="[pageToolbar]">
                <div class="flex flex-wrap items-center justify-between gap-3">
                    <p class="text-sm text-gray-500 dark:text-gray-400">
                        <ng-container *ngIf="dateFrom || dateTo; else allDates">
                            Filtered: {{ dateFrom || '…' }} – {{ dateTo || '…' }}
                        </ng-container>
                        <ng-template #allDates>All dates</ng-template>
                    </p>
                    <a
                        [routerLink]="['/audit-trail']"
                        [queryParams]="auditTrailQueryParams()"
                        class="text-sm font-medium text-primary hover:underline"
                    >
                        Open in audit trail
                    </a>
                </div>
            </ng-container>

            <div class="table-responsive mt-1 min-h-[22rem]">
                <div *ngIf="loading && !sessionItems.length" class="flex flex-col gap-3 py-4">
                    <div *ngFor="let i of [1,2,3,4,5]" class="h-10 animate-pulse rounded-xl bg-gray-100 dark:bg-white/5"></div>
                </div>

                <div *ngIf="!loading && !sessionItems.length" class="flex flex-col items-center justify-center gap-3 py-16 text-center">
                    <icon-notes class="size-12 text-gray-300 dark:text-gray-600" />
                    <p class="text-sm font-medium text-gray-500 dark:text-gray-400">No sessions found</p>
                    <p class="text-xs text-gray-400 dark:text-gray-500">Try widening the date range on the reports list.</p>
                </div>

                <table *ngIf="sessionItems.length" class="table table-hover table-surface">
                    <thead>
                        <tr>
                            <th>Date</th>
                            <th>Customer</th>
                            <th>Event</th>
                            <th class="text-center">Result</th>
                            <th class="text-center">Score</th>
                            <th>Runtime</th>
                            <th class="text-end">Action</th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr *ngFor="let s of sessionItems" class="border-none">
                            <td class="whitespace-nowrap">
                                <div class="text-sm font-medium text-gray-900 dark:text-white">{{ dateDisplay(s.created_at) }}</div>
                                <div class="text-xs text-gray-400">{{ timeDisplay(s.created_at) }}</div>
                            </td>
                            <td class="max-w-[10rem] truncate text-sm" [title]="s.customer_name ?? ''">{{ s.customer_name || '—' }}</td>
                            <td class="whitespace-nowrap text-sm">{{ eventLabel(s) }}</td>
                            <td class="whitespace-nowrap text-center">
                                <span *ngIf="s.result; else noResult"
                                    class="inline-flex items-center rounded-full px-2.5 py-1 text-xs font-semibold"
                                    [class]="statusClass(s.result)"
                                >
                                    {{ statusLabel(s.result) }}
                                </span>
                                <ng-template #noResult>
                                    <span class="text-xs text-gray-400">—</span>
                                </ng-template>
                            </td>
                            <td class="whitespace-nowrap text-center">
                                <span class="font-mono text-sm font-semibold"
                                    [class]="s.result === 'PASS' ? 'text-emerald-600 dark:text-emerald-400' : s.result === 'FAIL' ? 'text-red-600 dark:text-red-400' : 'text-gray-700 dark:text-gray-300'"
                                >{{ scoreDisplay(s.score) }}</span>
                            </td>
                            <td class="whitespace-nowrap font-mono text-xs text-gray-500">{{ durationDisplay(s.duration_seconds, s.duration_hours) }}</td>
                            <td class="text-end">
                                @if (canOpenSessionInAuditTrail(s)) {
                                <div class="inline-flex items-center justify-end gap-1">
                                    <button
                                        type="button"
                                        class="inline-flex h-9 w-9 items-center justify-center rounded-lg text-primary hover:bg-primary/10"
                                        title="View"
                                        aria-label="View"
                                        (click)="viewSession(s, $event)"
                                    >
                                        <icon-eye class="h-4 w-4" />
                                    </button>
                                </div>
                                }
                            </td>
                        </tr>
                    </tbody>
                </table>
            </div>

            <div *ngIf="sessionTotal > pageSize" class="mt-4 flex flex-wrap items-center justify-between gap-3">
                <p class="text-xs text-gray-500 dark:text-gray-400">
                    Showing {{ pageStart }}–{{ pageEnd }} of {{ sessionTotal }} sessions
                </p>
                <div class="flex items-center gap-1">
                    <button
                        *ngFor="let p of pageNumbers"
                        type="button"
                        class="flex size-8 items-center justify-center rounded-lg border text-sm font-medium"
                        [class]="p === page
                            ? 'border-[#303030] bg-[#303030] text-white dark:border-white/10 dark:bg-white/10'
                            : 'border-gray-200 bg-white hover:bg-gray-50 dark:border-[#191e3a] dark:bg-[#0e1726]'"
                        (click)="goToPage(p)"
                    >
                        {{ p }}
                    </button>
                    <button
                        type="button"
                        class="flex size-8 items-center justify-center rounded-lg border border-gray-200 bg-white text-sm hover:bg-gray-50 disabled:opacity-40 dark:border-[#191e3a] dark:bg-[#0e1726]"
                        [disabled]="page === totalPages || loading"
                        (click)="goToPage(page + 1)"
                        aria-label="Next page"
                    >
                        <icon-arrow-forward class="size-4" />
                    </button>
                </div>
            </div>
        </app-page-body-panel>
    </ng-container>
</div>
